import { Button, Stack, Typography } from '@mui/joy';
import { LockSimple, LockSimpleOpen, PencilSimple } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoCard } from 'core/components';
import { formatDate, getLabel } from 'core/utils';
import { UpdatePricingProfiles } from 'pricing/components';
import { PricingProfilePricingType } from 'pricing/enums';
import { IPricingProfile, PricingProfileAccessType } from 'pricing/types';
import { Direction, Level } from 'theme/enums';

interface IProps {
  pricingProfile: IPricingProfile;
  isReadOnly?: boolean;
}

export const PricingProfileDetailsInfo = ({ pricingProfile, isReadOnly = false }: IProps) => {
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { t } = useTranslation();

  const infoCardItems = useMemo(
    () => [
      { label: t('labelName'), value: pricingProfile.name },
      { label: t('labelAgreementId'), value: pricingProfile.agreementId },
      { label: t('labelType'), value: getLabel(pricingProfile.type) },
      {
        label: t('labelAccessType'),
        value: (
          <Stack alignItems="center" direction={Direction.ROW} gap={0.5}>
            {pricingProfile.accessType === PricingProfileAccessType.COMPANY ? (
              <>
                <LockSimpleOpen size={16} />
                {t('labelPublic')}
              </>
            ) : (
              <>
                <LockSimple size={16} weight="fill" />
                {t('labelPrivate')}
              </>
            )}
          </Stack>
        ),
      },
      { label: t('labelStartDate'), value: formatDate(pricingProfile.startDate) },
      { label: t('labelEndDate'), value: formatDate(pricingProfile.endDate) },
    ],
    [pricingProfile, t]
  );

  return (
    <Stack spacing={2}>
      <Stack sx={{ flexDirection: Direction.ROW, justifyContent: 'space-between' }}>
        <Typography level={Level.H4}>{t('titleMainInfo')}</Typography>
        {!isReadOnly && (
          <Button startDecorator={<PencilSimple />} onClick={() => setIsEditOpen(true)}>
            {t('buttonLabelEdit')}
          </Button>
        )}
      </Stack>

      <InfoCard items={infoCardItems} />

      <UpdatePricingProfiles
        type={PricingProfilePricingType.PRICING}
        pricingProfiles={[pricingProfile]}
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
      />
    </Stack>
  );
};
